// src/features/equipment/hooks/useEquipmentMaintenance.js
import { useCallback } from 'react';
import { equipmentAPI } from '../services/equipment';
import { useApi } from '../../../hooks/api/useApi';

/**
 * Maintenance cases for equipment. Every mutating call resolves with the
 * updated case (log entries and files included) in `result.data`.
 */
export function useEquipmentMaintenance() {
  const { loading, error, execute, clearError } = useApi();

  const listCases = useCallback((equipmentId, params = {}) =>
    execute(() => equipmentAPI.listEquipmentMaintenance(equipmentId, params)), [execute]);

  const getCase = useCallback((maintenanceId) =>
    execute(() => equipmentAPI.getMaintenanceCase(maintenanceId)), [execute]);

  const reportFault = useCallback((equipmentId, data) =>
    execute(() => equipmentAPI.reportFault(equipmentId, data)), [execute]);

  const recordMaintenance = useCallback((equipmentId, data) =>
    execute(() => equipmentAPI.createMaintenance(equipmentId, { ...data, mode: 'record' })), [execute]);

  const updateCase = useCallback((maintenanceId, data) =>
    execute(() => equipmentAPI.updateMaintenanceCase(maintenanceId, data)), [execute]);

  const pullReportedCase = useCallback((maintenanceId, data) =>
    execute(() => equipmentAPI.pullReportedCase(maintenanceId, data)), [execute]);

  const addNote = useCallback((maintenanceId, data) =>
    execute(() => equipmentAPI.addMaintenanceNote(maintenanceId, data)), [execute]);

  const completeCase = useCallback((maintenanceId, data) =>
    execute(() => equipmentAPI.completeMaintenance(maintenanceId, data)), [execute]);

  const cancelCase = useCallback((maintenanceId, data) =>
    execute(() => equipmentAPI.cancelMaintenance(maintenanceId, data)), [execute]);

  const attachFile = useCallback((maintenanceId, fileId) =>
    execute(() => equipmentAPI.attachMaintenanceFile(maintenanceId, { file_id: fileId })), [execute]);

  const removeFile = useCallback((maintenanceId, maintenanceFileId) =>
    execute(() => equipmentAPI.removeMaintenanceFile(maintenanceId, maintenanceFileId)), [execute]);

  return {
    loading,
    error,
    clearError,
    listCases,
    getCase,
    reportFault,
    recordMaintenance,
    updateCase,
    pullReportedCase,
    addNote,
    completeCase,
    cancelCase,
    attachFile,
    removeFile,
  };
}
